import { ExpectedError } from "../../expectedError";
import { oauthSetupSteps } from "../oauthSetupSteps";
import { customAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import logo from "./logo.svg";

function normalizeWechatToken(body: Record<string, unknown>) {
  if (body.errcode || typeof body.access_token !== "string") {
    throw new ExpectedError("WeChat rejected the token request");
  }
  return {
    ...body,
    token_type: "Bearer",
    access_token: JSON.stringify({
      access_token: body.access_token,
      ...(typeof body.openid === "string" ? { openid: body.openid } : {}),
    }),
    ...(typeof body.scope === "string"
      ? { scope: body.scope.split(",").join(" ") }
      : {}),
  };
}

const website = productVariant({
  identifier: "wechat",
  authLabel: "Website OAuth",
  protocol: "http",
  defaultUrlPattern: () =>
    "https://api\\.weixin\\.qq\\.com/sns/(?:userinfo|auth)(?:\\?.*)?$",
  auth: customAuth("wechat"),
  steps: oauthSetupSteps({
    identifier: "wechat",
    registrationUrl: "https://open.weixin.qq.com/",
    scopes: ["snsapi_login"],
    instructions:
      "Register an approved website application and configure its authorization callback domain. Enter the AppID as the Client ID and AppSecret as the client secret. Website OAuth uses a QR code. This authorization accesses user profiles; official account tokens use a separate API.",
  }),
  oauth: {
    grantType: () => "authorization_code",
    authorizationEndpoint: () =>
      "https://open.weixin.qq.com/connect/qrconnect#wechat_redirect",
    tokenEndpoint: () => "https://api.weixin.qq.com/sns/oauth2/access_token",
    refreshTokenEndpoint: () =>
      "https://api.weixin.qq.com/sns/oauth2/refresh_token",
    tokenEndpointAuthMethod: () => "client_secret_post",
    tokenRequestMethod: "GET",
    clientCredentialsFrom: "db",
    scopes: () => ["snsapi_login"],
    normalizeAuthorizationRequest: ({ client_id, scope, ...parameters }) => ({
      ...parameters,
      appid: client_id!,
      scope: scope!.split(" ").join(","),
    }),
    normalizeTokenRequest: ({
      client_id,
      client_secret,
      grant_type,
      code,
      refresh_token,
    }): Record<string, string> =>
      grant_type === "refresh_token"
        ? { appid: client_id!, grant_type, refresh_token: refresh_token! }
        : {
            appid: client_id!,
            secret: client_secret!,
            grant_type: grant_type!,
            code: code!,
          },
    normalizeTokenResponse: normalizeWechatToken,
    pkce: () => false,
  },
  connectionTest: {
    protocol: "http",
    request: () => ({
      url: "https://api.weixin.qq.com/sns/userinfo",
      expectedBodyContains: ['"openid"', '"nickname"'],
    }),
  },
  usageHint: () =>
    "Use GET https://api.weixin.qq.com/sns/userinfo or /sns/auth. The proxy supplies access_token and the authorizing user’s openid; omit both from workflow code. Website OAuth does not authorize official account messaging.",
});

const webpage = productVariant({
  identifier: "wechatWebpageOAuth",
  protocol: "http",
  defaultUrlPattern: website.defaultUrlPattern,
  auth: website.auth,
  connectionTest: website.connectionTest,
  usageHint: website.usageHint,
  authLabel: "Webpage OAuth",
  steps: oauthSetupSteps({
    identifier: "wechatWebpageOAuth",
    registrationUrl: "https://mp.weixin.qq.com/",
    scopes: ["snsapi_userinfo"],
    instructions:
      "Use your official account’s AppID and AppSecret, and configure its webpage authorization domain. Open this authorization flow inside the WeChat browser. This token accesses the authorizing user’s profile and is separate from the official account’s app access token.",
  }),
  oauth: {
    ...website.oauth!,
    clientCredentialsFrom: "db",
    authorizationEndpoint: () =>
      "https://open.weixin.qq.com/connect/oauth2/authorize#wechat_redirect",
    scopes: () => ["snsapi_userinfo"],
  },
});

export const wechat = defineProduct({
  name: "WeChat",
  tagLine: "User profiles and official account messaging",
  logo,
  tags: ["communication"],
  variants: [
    website,
    webpage,
    productVariant({
      identifier: "wechatOfficialAccount",
      authLabel: "Official account app credentials",
      protocol: "http",
      defaultUrlPattern: () => "https://api\\.weixin\\.qq\\.com/cgi-bin/.*",
      auth: customAuth("wechat"),
      steps: [
        {
          title: "Configure your official account",
          items: [
            {
              kind: "markdown",
              content:
                "In the [WeChat Developer Platform](https://developers.weixin.qq.com/), open your official account’s development settings and copy its AppID and AppSecret. Add your workflow’s outbound IP address to the account’s IP allowlist. Tokens are renewed automatically using the stable token API.",
            },
            {
              kind: "input",
              label: "AppID",
              required: true,
              target: { kind: "oauthConnection", field: "clientId" },
            },
            {
              kind: "input",
              label: "AppSecret",
              required: true,
              target: { kind: "oauthConnection", field: "clientSecret" },
            },
          ],
        },
      ],
      oauth: {
        grantType: () => "client_credentials",
        tokenEndpoint: () => "https://api.weixin.qq.com/cgi-bin/stable_token",
        tokenEndpointAuthMethod: () => "client_secret_post",
        tokenRequestFormat: () => "json",
        clientCredentialsFrom: "db",
        scopes: () => [],
        normalizeTokenRequest: ({ client_id, client_secret }) => ({
          grant_type: "client_credential",
          appid: client_id!,
          secret: client_secret!,
        }),
        normalizeTokenResponse: normalizeWechatToken,
      },
      connectionTest: {
        protocol: "http",
        request: () => ({
          url: "https://api.weixin.qq.com/cgi-bin/get_api_domain_ip",
          expectedBodyContains: '"ip_list"',
        }),
      },
      usageHint: () =>
        "Use the official account APIs under https://api.weixin.qq.com/cgi-bin/. The proxy adds access_token. Official account credentials do not authorize website user profiles.",
    }),
  ],
});
